import React, { useState, useEffect } from "react";
import axios from "../../../axiosInstance"; // Axios instance
import Sidebar_admin from "../Sidebar_Admin/Sidebar_admin"; // Sidebar
import "./AdminManagement.css"; // CSS File

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newAdmin, setNewAdmin] = useState({
    adminId: "",
    mobilenumber: "",
    password: "",
    role: "admin",
  });
  const [selectedAdmin, setSelectedAdmin] = useState(null); // For edit
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch admins from the server
  const fetchAdmins = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `/api/admin/usermanagement/readall?status=${statusFilter}&searchQuery=${searchQuery}`
      );
      setAdmins(response.data);
    } catch (err) {
      console.error("Error fetching admins:", err);
      setError("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch admins when status or search query changes
  useEffect(() => {
    fetchAdmins();
  }, [statusFilter, searchQuery]);

  // Add a new admin
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    const { adminId, mobilenumber, password, role } = newAdmin;

    if (!adminId || !mobilenumber || !password || !role) {
      setError("All fields are required!");
      return;
    }

    try {
      const response = await axios.post("/api/admin/usermanagement/add", {
        adminId,
        mobilenumber,
        password,
        role,
      });
      setNewAdmin({ adminId: "", mobilenumber: "", password: "", role: "admin" }); // Reset form fields
      fetchAdmins(); // Refresh admin list after adding
      alert(response.data.message);
    } catch (err) {
      console.error("Error adding admin:", err);
      setError("Failed to add admin");
    }
  };

  // Update admin status
  const handleUpdateStatus = async (adminId, status) => {
    try {
      const response = await axios.put(`/api/admin/usermanagement/edit/${adminId}/status`, {
        status,
      });
      fetchAdmins(); // Refresh admin list after updating status
      alert(response.data.message);
    } catch (err) {
      console.error("Error updating admin status:", err);
      setError("Failed to update admin status");
    }
  };

  // Delete an admin
  const handleDeleteAdmin = async (adminId) => {
    try {
      const response = await axios.delete(`/api/admin/usermanagement/delete/${adminId}`);
      fetchAdmins(); // Refresh admin list after deleting
      alert(response.data.message);
    } catch (err) {
      console.error("Error deleting admin:", err);
      setError("Failed to delete admin");
    }
  };

  // Handle edit admin (set selected admin)
  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
  };

  // Handle form for editing an admin
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    const { adminId, mobilenumber, password, role } = selectedAdmin;

    if (!adminId || !mobilenumber || !password || !role) {
      setError("All fields are required!");
      return;
    }

    try {
      const response = await axios.put(`/api/admin/usermanagement/edit/${adminId}`, {
        adminId,
        mobilenumber,
        password,
        role,
      });
      setSelectedAdmin(null); // Reset form fields
      fetchAdmins(); // Refresh admin list after editing
      alert(response.data.message);
    } catch (err) {
      console.error("Error editing admin:", err);
      setError("Failed to edit admin");
    }
  };

  return (
    <div>
      <Sidebar_admin />
      <div className="admin-panel">
        <h2 className="admin-panel__title">Admin Management</h2>

        {/* Search and Filter */}
        <div className="admin-panel__search">
          <input
            type="text"
            className="admin-panel__search-input"
            placeholder="Search by Mobile Number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="admin-panel__filters">
          <button
            className={`admin-panel__filter-button ${statusFilter === "active" ? "active" : ""}`}
            onClick={() => setStatusFilter("active")}
          >
            Active
          </button>
          <button
            className={`admin-panel__filter-button ${statusFilter === "inactive" ? "active" : ""}`}
            onClick={() => setStatusFilter("inactive")}
          >
            Inactive
          </button>
          <button
            className={`admin-panel__filter-button ${statusFilter === "All" ? "active" : ""}`}
            onClick={() => setStatusFilter("All")}
          >
            All
          </button>
        </div>

        {/* Add Admin Form */}
        <div className="admin-panel__form">
          <h3>{selectedAdmin ? "Edit Admin" : "Add New Admin"}</h3>
          <form onSubmit={selectedAdmin ? handleEditFormSubmit : handleAddAdmin}>
            <input
              type="text"
              placeholder="Admin ID"
              value={selectedAdmin ? selectedAdmin.adminId : newAdmin.adminId}
              onChange={(e) => {
                if (selectedAdmin) {
                  setSelectedAdmin({ ...selectedAdmin, adminId: e.target.value });
                } else {
                  setNewAdmin({ ...newAdmin, adminId: e.target.value });
                }
              }}
            />
            <input
              type="text"
              placeholder="Mobile Number"
              value={selectedAdmin ? selectedAdmin.mobilenumber : newAdmin.mobilenumber}
              onChange={(e) => {
                if (selectedAdmin) {
                  setSelectedAdmin({ ...selectedAdmin, mobilenumber: e.target.value });
                } else {
                  setNewAdmin({ ...newAdmin, mobilenumber: e.target.value });
                }
              }}
            />
            <input
              type="password"
              placeholder="Password"
              value={selectedAdmin ? selectedAdmin.password : newAdmin.password}
              onChange={(e) => {
                if (selectedAdmin) {
                  setSelectedAdmin({ ...selectedAdmin, password: e.target.value });
                } else {
                  setNewAdmin({ ...newAdmin, password: e.target.value });
                }
              }}
            />
            <select
              value={selectedAdmin ? selectedAdmin.role : newAdmin.role}
              onChange={(e) => {
                if (selectedAdmin) {
                  setSelectedAdmin({ ...selectedAdmin, role: e.target.value });
                } else {
                  setNewAdmin({ ...newAdmin, role: e.target.value });
                }
              }}
            >
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
            <button type="submit">{selectedAdmin ? "Update Admin" : "Add Admin"}</button>
          </form>
        </div>

        {/* Display Admin List */}
        {loading ? (
          <p className="admin-panel__loading">Loading admins...</p>
        ) : error ? (
          <p className="admin-panel__error">{error}</p>
        ) : (
          <table className="admin-panel__table">
            <thead>
              <tr>
                <th className="admin-panel__table-header">Admin ID</th>
                <th className="admin-panel__table-header">Mobile Number</th>
                <th className="admin-panel__table-header">Role</th>
                <th className="admin-panel__table-header">Status</th>
                <th className="admin-panel__table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.adminId} className="admin-panel__table-row">
                  <td>{admin.adminId}</td>
                  <td>{admin.mobilenumber}</td>
                  <td>{admin.role}</td>
                  <td>{admin.status}</td>
                  <td>
                    <button onClick={() => handleEditAdmin(admin)}>Edit</button>
                    <button
                      onClick={() => handleUpdateStatus(admin.adminId, admin.status === "active" ? "inactive" : "active")}
                    >
                      {admin.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => handleDeleteAdmin(admin.adminId)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
